import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { telemetryProductFromInput } from '@order/Telemetry';

import { useAddLineItemToCart } from '../useCart';

// ─────────────────────────────────────────────────────────────────────────────

export const useOneClickAddToBag = (params: UseOneClickAddToBagParams) => {
  const { restaurantSlug, deliveryDetails, onSuccess, onError } = params;

  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const trackProductAddSuccess = useCallback<AddLineItemToCartSuccess>(
    (payload) => {
      const { product, quantity } = payload;

      const mappedProduct = telemetryProductFromInput({
        ...product,
        quantity,
        location: restaurantSlug,
      });

      onSuccess?.(mappedProduct);
    },
    [onSuccess, restaurantSlug],
  );

  const showProductAddSuccessBanner = useCallback<AddLineItemToCartSuccess>(
    (payload) => {
      const { product, quantity } = payload;

      const bannerText = formatMessage(messages.successMessage, {
        product_name: product.name,
        quantity,
      });

      addNoticeBanner({
        text: bannerText,
        autoHideTimeout: 2500,
        palette: 'success',
      });
    },
    [addNoticeBanner, formatMessage],
  );

  const onProductAddSuccess = useCallback<AddLineItemToCartSuccess>(
    (payload) => {
      trackProductAddSuccess(payload);
      showProductAddSuccessBanner(payload);
    },
    [showProductAddSuccessBanner, trackProductAddSuccess],
  );

  const onProductAddSuccessFail = useCallback<AddLineItemToCartError>(
    (payload) => {
      onError?.(payload.message);
    },
    [onError],
  );

  const { addLineItemToCart, fetching } = useAddLineItemToCart({
    onSuccess: onProductAddSuccess,
    onError: onProductAddSuccessFail,
  });

  const addProductToBag = useCallback(
    async (product: PartialProduct, quantity: number) => {
      return addLineItemToCart({ product, quantity, deliveryDetails });
    },
    [addLineItemToCart, deliveryDetails],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    addProductToBag,
    isAddingProductToBag: fetching,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  successMessage: {
    defaultMessage: '{quantity}x {product_name} added to your bag!',
    description: '1-click add to bag | Success message',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOneClickAddToBagParams = {
  restaurantSlug: string;
  deliveryDetails?: AddLineItemToCartInput['deliveryDetails'];
  onSuccess?: (selectedProduct: ReturnType<typeof telemetryProductFromInput>) => void; // prettier-ignore
  onError?: (errorMessage?: string) => void;
};

type AddLineItemToCartSuccess = NonNullable<AddLineItemToCartParams['onSuccess']>;

type AddLineItemToCartError = NonNullable<AddLineItemToCartParams['onError']>;

type AddLineItemToCartParams = NonNullable<Parameters<typeof useAddLineItemToCart>[0]>;

type AddLineItemToCartInput = Parameters<
  ReturnType<typeof useAddLineItemToCart>['addLineItemToCart']
>[0];

type PartialProduct = { id: string; name: string };
